import { create } from "zustand";
import { devtools } from "zustand/middleware";

import { NO_META } from "@/constants";
import { getStudent, getStudentByEmail, getStudentById } from "@/services/http/members";
import { GetStudentByEmailInput, GetStudentsFiltersOptionsInputList } from "@_types/students";

import { StudentsStoreActions, StudentsStoreProps } from "./ListStudents.types";

export const useStudentsStore = create<StudentsStoreProps & StudentsStoreActions>()(
  devtools((set, get) => ({
    studentList: [],
    meta: NO_META,
    isLoading: false,
    isStudentLoading: false,
    params: {
      pageSize: 10,
      page: 1,
    },
    activeFilters: [],
    student: null,

    setStudents: (data, metaData) => set({ studentList: data, meta: metaData }),
    setStudent: (data, metaData) => set({ student: data, meta: metaData }),

    fetchStudent: async incomingParams => {
      set({ isLoading: true });

      const { params } = get();

      try {
        const hasSearchParams =
          incomingParams &&
          Object.keys(incomingParams).some(key => {
            if (key === "page" || key === "limit") return false;
            const value = incomingParams[key as keyof typeof incomingParams];
            return value !== undefined && value !== null && value !== "";
          });

        const finalParams = hasSearchParams
          ? { ...params, ...incomingParams, page: 1 }
          : { ...params, ...incomingParams };

        const { data: response } = await getStudent(finalParams);
        const { data, meta } = response;
        set({ studentList: data, meta });
      } catch (error) {
        set({ studentList: [], meta: NO_META });
      } finally {
        set({ isLoading: false });
      }
    },

    setIsLoading: state => set({ isLoading: state }),

    paginate: page => {
      const { params, fetchStudent } = get();
      set({ params: { ...params, page } });
      fetchStudent();
    },

    search: search => {
      const { params, fetchStudent } = get();
      set({ params: { ...params, search, page: 1 } });
      search.length === 0 && set({ params: { ...params, search: undefined } });
      fetchStudent();
    },

    addFilter: newFilter => {
      const { activeFilters, filterToParams } = get();
      const foundFilter = activeFilters.find(filter => filter.field === newFilter.field);
      if (foundFilter !== undefined) {
        set({
          activeFilters: [...activeFilters.filter(item => item.field !== newFilter.field), newFilter],
        });
      } else {
        set({
          activeFilters: [...activeFilters, newFilter],
        });
      }
      filterToParams();
    },

    getFilter: field => {
      const { activeFilters } = get();
      return activeFilters.find(filter => filter.field === field);
    },

    removeFilter: index => {
      const { activeFilters, filterToParams } = get();
      const newFilters = activeFilters.filter((_, i) => i !== index);
      set({ activeFilters: newFilters });
      filterToParams();
    },

    removeFilterByName: field => {
      const { activeFilters, filterToParams } = get();
      let newFilters = activeFilters.filter(item => item.field !== field);
      if (field === "dateOfPurchaseFromDate") {
        newFilters = newFilters.filter(item => item.field !== "dateOfPurchaseToDate");
      }
      if (field === "lastAccessDateFromDate") {
        newFilters = newFilters.filter(item => item.field !== "lastAccessDateToDate");
      }
      set({ activeFilters: newFilters });
      filterToParams();
    },

    clearFilters: () => {
      const { filterToParams } = get();
      set({ activeFilters: [] });
      filterToParams();
    },

    filterToParams: () => {
      const { activeFilters, params } = get();
      if (activeFilters.length > 0) {
        const filterParams: Record<GetStudentsFiltersOptionsInputList, string> = activeFilters.reduce(
          (acc, { field, value }) => {
            acc[field] = value;
            return acc;
          },
          {} as Record<GetStudentsFiltersOptionsInputList, string>
        );
        set({ params: { ...params, ...filterParams } });
      } else {
        set({ params: { page: params.page, pageSize: params.pageSize, search: params.search } });
      }
    },

    resetParams: incomingParams => {
      const { params } = get();
      set({ params: { ...params, ...incomingParams } });
    },

    fetchStudentByEmail: async ({ email }: GetStudentByEmailInput) => {
      set({ isLoading: true });

      try {
        const { data } = await getStudentByEmail({ email });
        const [student] = data.data;

        set({ student });
      } catch (error) {
        set({ student: null });
      } finally {
        set({ isLoading: false });
      }
    },

    fetchStudentById: async data => {
      set({ student: data });
      try {
        set({ isStudentLoading: true });
        const { data: response } = await getStudentById(data.uuid);
        set({ student: response });
      } catch (error) {
      } finally {
        set({ isStudentLoading: false });
      }
    },
  }))
);
